import React, { useState, useEffect } from 'react';
import {
  Form,
  InputNumber,
  Select,
  Button,
  Card,
  Row,
  Col,
  message,
  Spin,
  Alert,
  Space,
  Divider
} from 'antd';
import { SaveOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ContainerFormData,
  ContainerFormProps,
  ContainerSpec
} from '../../../types/container';
import { getMockContainerSpecsResponse } from '../../../mock/containerData';

const { Option } = Select;

/**
 * ContainerForm Component
 * Handles both create and edit operations for containers
 */
const ContainerForm: React.FC<ContainerFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isEdit = false,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [containerSpecs, setContainerSpecs] = useState<ContainerSpec[]>([]);
  const [specsLoading, setSpecsLoading] = useState(true);
  const { t } = useTranslation();

  // Load container specifications
  useEffect(() => {
    const loadContainerSpecs = async () => {
      try {
        setSpecsLoading(true);
        const response = getMockContainerSpecsResponse();
        if (response.success && response.data) {
          setContainerSpecs(response.data);
        }
      } catch (error) {
        console.error('Error loading container specs:', error);
        message.error(t('container.loadSpecsFailed'));
      } finally {
        setSpecsLoading(false);
      }
    };

    loadContainerSpecs();
  }, [t]);

  // Set initial values when provided
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    try {
      setError(null);
      const values = await form.validateFields();

      // Prepare form data
      const formData: ContainerFormData = {
        containerSpecCode: values.containerSpecCode,
        quantity: values.quantity,
        l4Code: "a8402435-bd7b-47a0-aa2b-9f4860",
        prefixUseSpecCode: "true",
        processDefKey: "wms.base-data.container.custom.create"
      };

      setSubmitting(true);
      await onSubmit(formData);

      const successMessage = isEdit ? t('container.updateSuccess') : t('container.createSuccess');
      message.success(successMessage);
    } catch (error: any) {
      console.error('Form submission error:', error);

      if (error.errorFields) {
        // Form validation errors
        const firstError = error.errorFields[0];
        message.error(`${firstError.name[0]}: ${firstError.errors[0]}`);
      } else if (error.message) {
        setError(error.message);
      } else {
        const failedMessage = isEdit ? t('container.updateFailed') : t('container.createFailedRetry');
        setError(failedMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle form cancellation
   */
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
    setError(null);
  };

  const isLoading = loading;
  const isSubmitDisabled = submitting || isLoading;

  return (
    <Card
      title={
        <Space>
          {isEdit ? t('container.editTitle') : t('container.createTitle')}
          {isLoading && <LoadingOutlined />}
        </Space>
      }
      className="container-form-card"
    >
      <Spin spinning={isLoading} tip={t('container.loadingTip')}>
        {error && (
          <Alert
            message={t('container.operationFailed')}
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          className="container-form"
        >
          <Row gutter={[16, 0]}>
            {/* Container Code - only show for create mode */}

            {/* Usage Status */}
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="containerSpecCode"
                label={t('container.containerSpec')}
                rules={[{ required: true, message: t('container.selectContainerSpec') }]}
              >
                <Select
                  placeholder={t('container.selectContainerSpec')}
                  disabled={isSubmitDisabled || specsLoading}
                  loading={specsLoading}
                >
                  {containerSpecs.map(spec => (
                    <Option key={spec.code} value={spec.code}>
                      {spec.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>



            {/* quantity */}
            <Col xs={24}>
              <Form.Item
                name="quantity"
                label={t('container.quantity')}
                rules={[
                  { required: true, message: t('container.enterQuantity') },
                  { type: 'number', min: 1, max: 500, message: t('container.quantityRange') }
                ]}
              >
                <InputNumber
                  placeholder={t('container.quantityPlaceholder')}
                  min={1}
                  max={500}
                  step={1}
                  precision={0}
                  style={{ width: '100%' }}
                  disabled={isSubmitDisabled}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          {/* Form Actions */}
          <Row justify="end">
            <Space>
              <Button onClick={handleReset} disabled={isSubmitDisabled}>
                {t('container.reset')}
              </Button>
              <Button onClick={handleCancel} disabled={submitting}>
                <CloseOutlined />
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={isSubmitDisabled}
                icon={<SaveOutlined />}
              >
                {isEdit ? t('common.edit') : t('common.add')}
              </Button>
            </Space>
          </Row>
        </Form>
      </Spin>
    </Card>
  );
};

export default ContainerForm;